import { useState, useEffect } from 'react'
import { KeyRound, Save, CheckCircle2, AlertTriangle, User as UserIcon } from 'lucide-react'
import { supabase, isDemoMode } from '../lib/supabase'

// Self-service password change (works for both staff and client roles).
// Available to any signed-in user from the sidebar.
export default function Account() {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState(null)

  useEffect(() => {
    if (isDemoMode) return
    supabase.auth.getUser().then(({ data }) => {
      const u = data?.user
      if (u) {
        setEmail(u.email || '')
        setName(u.user_metadata?.full_name || u.email?.split('@')[0] || '')
      }
    })
  }, [])

  const changePassword = async (e) => {
    e.preventDefault()
    setResult(null)
    if (newPw.length < 4) {
      return setResult({ ok: false, message: 'Password must be at least 4 characters.' })
    }
    if (newPw !== confirmPw) {
      return setResult({ ok: false, message: 'The two password fields don\'t match.' })
    }
    if (isDemoMode) return
    setSaving(true)
    // Guard against stale sessionStorage state — if we appear "signed in"
    // in the app but supabase has no session (e.g. after a demo-mode login
    // carried across a redeploy), updateUser would fail silently and the
    // user would just see the button do nothing. Detect and explain.
    const { data: sessionCheck } = await supabase.auth.getSession()
    if (!sessionCheck.session) {
      setSaving(false)
      return setResult({
        ok: false,
        message: 'Your session has expired. Please sign out (bottom-left) and sign in again, then try changing your password.',
      })
    }
    // password_set flag tells App.jsx not to route this user to the
    // set-password screen on next load. Safe to set unconditionally here
    // — anyone reaching this page is signed in and just chose a password.
    const { error } = await supabase.auth.updateUser({
      password: newPw,
      data: { password_set: true },
    })
    setSaving(false)
    if (error) return setResult({ ok: false, message: error.message })
    setResult({ ok: true, message: 'Password updated. The new one is active right away.' })
    setNewPw('')
    setConfirmPw('')
  }

  return (
    <div className="space-y-4 max-w-xl">
      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <UserIcon className="w-5 h-5 text-brand" />
          Your account
        </h3>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Signed in as</div>
            <div className="font-medium text-gray-800 truncate">{name || '—'}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Email</div>
            <div className="font-medium text-gray-800 truncate">{email || '—'}</div>
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-brand" />
          Change password
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Pick something only you know. Stays active straight away.
        </p>

        <form onSubmit={changePassword} className="space-y-3 max-w-md">
          <label className="block">
            <span className="text-xs text-gray-500 block mb-1">New password</span>
            <input
              type="password"
              value={newPw}
              onChange={e => setNewPw(e.target.value)}
              className="input-field"
              autoComplete="new-password"
              required
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray-500 block mb-1">Confirm new password</span>
            <input
              type="password"
              value={confirmPw}
              onChange={e => setConfirmPw(e.target.value)}
              className="input-field"
              autoComplete="new-password"
              required
            />
          </label>
          <button type="submit" disabled={saving} className="btn-primary">
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Update password'}
          </button>
        </form>

        {result && (
          <div className={`mt-4 p-3 rounded-lg text-sm flex items-start gap-2 ${result.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`}>
            {result.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
            {result.message}
          </div>
        )}
      </div>
    </div>
  )
}
